import { Record, Map, List, OrderedMap } from "immutable";
import { DisbursalRequestRecord, DisbursalJobRecord } from "./invoices";
import {
    NATURE_OF_ASSOCIATION_TYPES,
    APPLICANT_TYPES,
    ACCOUNT_TYPES,
    RESIDENCE_TYPES,
    GENDER_TYPES,
    COMPANY_TYPES,
    VALUE_TYPE,
    ADDRESSES_TYPES
} from "constants";
import { CONSTITUTION_TYPES, ERP_DATASOURCE, DB_TAGGING, CORPORATION_TYPE } from "constants/enums";
import moment from "moment";

const SUBVENTION_FROM = {
    1: "Invoice Date",
    2: "Disbursal Date"
};

/** Matches `Term_Loan` in Additional Product Applicable (value 7). */
const ADDITIONAL_PRODUCT_TERM_LOAN = 7;

function additionalProductsIncludeTermLoan(products) {
    if (products == null) {
        return false;
    }
    const arr = List.isList(products)
        ? products.toArray()
        : Array.isArray(products)
            ? products
            : [products];
    return arr.some(p => Number(p) === ADDITIONAL_PRODUCT_TERM_LOAN);
}

export {
    DisbursalRequestRecord, 
    DisbursalJobRecord
};

export class CompanyLosErpDetail extends Record({
    id: null,
    company: null,
    anchor: null,
    erp_data_source: null,
    erp_type: null,
    erp_version: null,
    erp_company_code: null,
    erp_company_id: null,
    is_active: null
}) {
    get erpDataSource() {
        if(this.erp_data_source){
            return ERP_DATASOURCE.findEntry(_ => _ == this.erp_data_source)[0] || "--";
        }
        return "--";
    }
    get erpDetails() {
        return ({
            erp_data_source: this.erpDataSource,
            erp_version: (this.erp_version && "Version "+this.erp_version) || "--"
        });
    }
}

export class CompanyDetail extends Record({
    id: null,
    company: null,
    anchor: null,
    location: null,
    anchor_asm: null,
    anchor_yoa: null,
    establishment_year: null,
    headquarter: null,
    total_no_of_dealers: null,
    dealers_with_regular_billing: null,
    territory_managed: null,
    current_market_os: null,//trade terms
    credit_days: null,//trade terms
    cash_discount: null,//trade terms
    cash_discount_days: null,//trade terms
    manpower: null,//manpower
    average_dealer_lor_in_months: null,//dependence
    average_dealer_sales_contribution: null,//dependence
    future_plan: null,//others
    other_info: null,//others
    remarks_progcap_rm: null,//others
    ordering_ways: null,//ordering,
    dealer_profile_turnover_to: null,
    dealer_profile_turnover_from: null,
    program_size: null,
    credit_remarks: null,
    profile: null,
    program_anchor: null,
    unit: null
}) {

}

export class CompanyAnnualTurnover extends Record({
    id: null,
    company: null,
    anchor: null,
    financial_year: null,
    end_date: null,
    amount: null
}) {

    get getAnchor() {
        if(this.anchor){
            return this.anchor.name || "--";
        }
        return "--";
    }

    get financialYear (){
        if(this.financial_year){
            return this.financial_year.name || "--";
        }
        return "--";
    }

    get annualTurnover(){
        return ({
            anchor: this.getAnchor,
            financial_year: this.financialYear,
            end_date: (this.end_date && moment(this.end_date).format("Do MMM YYYY")) || "--",
            amount: this.amount || "--"
        });
    }
}

export class CompanyBankingFacility extends Record({
    id: null,
    company: null,
    bank: null,
    facility_name: null,
    sanction_limit: null,
    sanction_year: null,
    security_or_collateral: null
}) {
    get bankName() {
        if(this.bank){
            return this.bank.name || "--";
        }
        return "--";
    }

    get bankingFacility() {
        return ({
            company: this.company || "--",
            bank: this.bankName,
            facility_name: this.facility_name || "--",
            sanction_limit: this.sanction_limit || "--",
            sanction_year: this.sanction_year || "--",
            security_or_collateral: this.security_or_collateral || "--"
        });
    }
}

export class ERPSoftware extends Record({
    id: null,
    name: null,
    short_name: null
}) {

}

export class CompanyBrand extends Record({
    id: null,
    channel_product: null,
    company: null,
    name: null,
    value_type: null,
    amount: null,
    percentage: null,
    subvention_perc: null,
    subvention_days: null,
    is_primary: false
}) {
    get channelProduct() {
        if(this.channel_product){
            return this.channel_product.name;
        }
        return "--";
    }

    get valueType() {
        return VALUE_TYPE.findEntry(_ => _ == this.value_type)[0] || "--";
    }

    get companyBrand() {
        return ({
            channel_product: this.channelProduct,
            name: this.name || "--",
            value_type: this.valueType,
            amount: this.amount || "--",
            percentage: (this.percentage && this.percentage+"%") || "--",
            subvention_perc: (this.subvention_perc && this.subvention_perc+"%") || "--",
            subvention_days: (this.subvention_days && this.subvention_days+"%") || "--" ,
            is_primary: this.is_primary ? "Yes" : "No"
        });
    }
}

export class CompanyProfileRecord extends Record({
    id: null,
    addresses: new List([]),
    associates: new List([]),
    bankAccounts: new List([]),
    bank_accounts: null,
    name: null,
    short_name: null,
    nature_of_business: [],
    type: null,
    date_of_incorporation: null,
    place_of_incorporation: null,
    business_start_date: null,
    constitution: null,
    annual_turnover1_date: null,
    annual_turnover1: null,
    annual_turnover2_date: null,
    annual_turnover2: null,
    pan: null,
    gstin: null,
    cin: null,
    tin: null,
    registration_num: null,
    business_category: null,
    industry_type: null,
    num_of_employees: null,
    credit_rating: null,
    credit_rating_date: null,
    credit_rating_expiry_date: null,
    is_anchor: false,
    is_seller: false,
    is_buyer: false,
    current_status: null,
    current_stage : null,
    action_task_id : null,
    company_cibil_score: null,
    channel_products: [],
    secondary_relationship_manager: [],
    company_cibil_remarks: null,
    mis_email_recipients: [],
    is_main_borrower: null,
    shareholding: null,
    is_active: true,
    created_by: null,
    modified_by: null,
    create_date: null,
    modified_date: null,
    primary_relationship_manager: null,
    program_anchor: null,
    vp_sales: null,
    zonal_head: null,
    parent_industry: null,
    industry: null,
    is_corporate_agent: null,
    distributor_service_manager: null,
    type_of_scheme: null,
    annual_turnover: null,
    total_no_of_dealers: null,
    esign_status : null,
    is_d2r_db: null,
    is_mpa_approval_required: null,
    additional_product_applicable: null,
    is_bundled_mandatory: false,
    term_loan_program_type: null,
    ums_company_id: null,
    corporation_type: null,
    db_tagging: null,
}) {

    get constitutionType() {
        return this.constitution && CONSTITUTION_TYPES.findKey(_ => _ === this.constitution);
    }

    get secondary_relationship_managers() {
        return this.secondary_relationship_manager && this.secondary_relationship_manager.join(", ");
    }

    get primary_relationship_managers() {
        return this.primary_relationship_manager && this.primary_relationship_manager.full_name;
    }

    get vp_sales_manager() {
        return this.vp_sales && this.vp_sales.full_name;
    }

    get zonal_head_manager() {
        return this.zonal_head && this.zonal_head.full_name;
    }

    get operating_address() {
        return this.addresses.find(
            _ => _ && _.address_type === ADDRESSES_TYPES.get("Office")
        );
    }

    get registered_address() {
        return this.addresses.find(
            _ => _ && _.address_type === ADDRESSES_TYPES.get("Registered")
        );
    }

    get distributor_service_managers() {
        return this.distributor_service_manager && this.distributor_service_manager.full_name;
    }

    get json() {
        return {
            id: this.id,
            gstin: this.gstin,
            pan: this.pan,
            name: this.name,
            type: Number(this.type),
            channel_products: this.channel_products.map(_ => _.id),
            mis_email_recipients: this.mis_email_recipients,
            secondary_relationship_manager: this.secondary_relationship_manager.map(_ => +_.id),
            primary_relationship_manager: this.primary_relationship_manager && this.primary_relationship_manager.id,
            vp_sales:
                this.vp_sales == null
                    ? null
                    : typeof this.vp_sales === "number"
                        ? this.vp_sales
                        : this.vp_sales.id,
            zonal_head:
                this.zonal_head == null
                    ? null
                    : typeof this.zonal_head === "number"
                        ? this.zonal_head
                        : this.zonal_head.id,
            parent_industry:
                this.parent_industry == null
                    ? null
                    : typeof this.parent_industry === "number"
                        ? this.parent_industry
                        : this.parent_industry.id,
            industry_head: (() => {
                const pi = this.parent_industry;
                if (pi == null) {
                    return null;
                }
                if (typeof pi === "number") {
                    return null;
                }
                const ih = pi.industry_head;
                if (ih == null) {
                    return null;
                }
                return typeof ih === "number" ? ih : ih.id;
            })(),
            program_anchor: this.program_anchor && this.program_anchor.id,
            industry: this.industry && this.industry.id,
            is_corporate_agent: this.is_corporate_agent,
            distributor_service_manager: this.distributor_service_manager && this.distributor_service_manager.id,
            additional_product_applicable: this.additional_product_applicable,
            is_bundled_mandatory: (() => {
                const t = Number(this.type);
                if (t !== 1 && t !== 2) {
                    return false;
                }
                if (this.gstin && String(this.gstin).trim().startsWith("99")) {
                    return false;
                }
                if (!additionalProductsIncludeTermLoan(this.additional_product_applicable)) {
                    return false;
                }
                return Boolean(this.is_bundled_mandatory);
            })(),
            term_loan_program_type: this.term_loan_program_type,
            corporation_type: this.corporation_type == '-' ? 0 : typeof this.corporation_type === "string" ? CORPORATION_TYPE.get(this.corporation_type) : this.corporation_type,
        };
    }

    get form() {
        return new Map({
            id: this.id,
            gstin: this.gstin,
            name: this.name,
            type: String(this.type),
            channel_products: this.channel_products,
            mis_email_recipients: this.mis_email_recipients,
            secondary_relationship_manager: this.secondary_relationship_manager,
            primary_relationship_manager: this.primary_relationship_manager,
            program_anchor: this.program_anchor,
            vp_sales:
                Number(this.type) === 1 || Number(this.type) === 2
                    ? (this.vp_sales &&
                          typeof this.vp_sales === "object" &&
                          this.vp_sales.full_name) ||
                      ""
                    : this.vp_sales,
            zonal_head:
                Number(this.type) === 1 || Number(this.type) === 2
                    ? (this.zonal_head &&
                          typeof this.zonal_head === "object" &&
                          this.zonal_head.full_name) ||
                      ""
                    : this.zonal_head,
            parent_industry:
                (this.parent_industry && this.parent_industry.name) || "",
            industry_head:
                (this.parent_industry &&
                    this.parent_industry.industry_head &&
                    this.parent_industry.industry_head.full_name) ||
                "",
            industry: this.industry,
            is_corporate_agent: this.is_corporate_agent,
            distributor_service_manager: this.distributor_service_manager,
            annual_turnover: this.annual_turnover,
            total_no_of_dealers: this.total_no_of_dealers,
            additional_product_applicable: this.additional_product_applicable,
            is_bundled_mandatory: Boolean(this.is_bundled_mandatory),
            term_loan_program_type: this.term_loan_program_type,
            corporation_type: this.corporation_type == "-" ? 0 : this.corporation_type,
            db_tagging: DB_TAGGING.findKey(_ => _== this.db_tagging),

        });
    }



    get spoc_user_json() {
        // const spoc_user = (this.associates || new List([])).filter(_ => {
        //     return _.is_key_contact;
        // });
        const spoc_user = (this.associates || new List([]));
        if (spoc_user) {
            return spoc_user.map(_ => (
                {
                    id: _.id,
                    first_name: _.first_name,
                    last_name: _.last_name,
                    email: _.email,
                    phone: _.phone,
                    gender: _.gender,
                    type: _.type,
                    designation: _.designation,
                    nature_of_association: _.nature_of_association,
                    pan: _.pan,
                    is_db_mou_triggered: _.is_db_mou_triggered,
                    is_key_contact: true,
                    is_main_borrower: true,
                    company: this.id,
                    is_active: _.is_active,
                    is_d2r_guarantor : _.is_d2r_guarantor || undefined
                }
            ));
        }
        return null;
    }

    get spoc_user_form() {
        // const spoc_user = (this.associates || new List([])).filter(_ => _.is_key_contact);
        const spoc_user = (this.associates || new List([]));
        if (spoc_user) {
            return spoc_user.map(_ => ({
                id: _.id,
                is_key_account: _.is_key_account,
                is_key_contact: _.is_key_contact,
                first_name: _.first_name,
                last_name: _.last_name,
                email: _.email,
                phone: _.phone,
                type: _.type,
                gender: _.gender,
                pan: _.pan,
                is_db_mou_triggered: _.is_db_mou_triggered,
                designation: _.designation,
                nature_of_association: _.nature_of_association,
                is_active: this.is_active,
                is_d2r_guarantor: String(_.is_d2r_guarantor)
            }));
        }
        return null;
    }

    get disbursal_bank_account() {
        const disbursal_account = (this.bank_accounts || new Map({})).find(_ => [5,8].includes(_.account_type));
        if(disbursal_account) {
            return new Map({
                id: disbursal_account.id,
                company: disbursal_account.company,
                account_holder_name: disbursal_account.account_holder_name,
                account_number: disbursal_account.account_number,
                bank_branch: disbursal_account.bank_branch,
                ifsc: disbursal_account.bank_branch,
                associate: disbursal_account.associate,
                account_type: disbursal_account.account_type,
                account_limit: disbursal_account.account_limit,
                status: disbursal_account.status,
                bank_cheques: disbursal_account.bank_cheques,
                bank_nach: disbursal_account.bank_nach,
                is_active: disbursal_account.is_active,
                is_key_account: disbursal_account.is_key_account,
                created_by: disbursal_account.created_by,
                modified_by: disbursal_account.modified_by,
                create_date: disbursal_account.create_date,
                modified_date: disbursal_account.modified_date
            });
        }
        return null;
    }

    get create_date_formatted() {
        if(this.create_date) {
            return this.create_date.format("DD MMM YY");
        }
        return  null;
    }

    get companyType() {
        return COMPANY_TYPES.findKey(_ => _ === +(this.type));
    }


    get primaryDescriptionList() {
        return new Map({
            "Creation Date": this.create_date_formatted
        });
    }

    updateSpocUser(spocValues) {
        let updatedValues = spocValues.toList().toArray();
        updatedValues = updatedValues.map(_ => {
            const associate = this.associates.find(__ => _.id === __.id);
            if(associate) {
                return {
                    ...associate,
                    ..._
                };
            }
            return _;
        });
        return this.merge("associates", new List([...this.associates, ...updatedValues]));
    }

    updateDisbursalAccount(bankDetails) {
        const disbursalAccountId = (this.bank_accounts.find(_ => _.account_type === 8) || {}).id;
        if(disbursalAccountId && disbursalAccountId !== -1) {
            return this.setIn(["bank_accounts", disbursalAccountId], new BankAccountRecord({...bankDetails, id: disbursalAccountId}));
        }
        if(this.bank_accounts && (this.bank_accounts.size || this.bank_accounts.length)) {
            return this.set("bank_accounts", new List([...this.bank_accounts.toList().toArray(), new BankAccountRecord(bankDetails)]).toMap());
        } else {
            return this.set("bank_accounts", new List([new BankAccountRecord(bankDetails)]).toMap());
        }
    }

    updateBankAccounts(bankDetails) {
        const disbursalAccountId = (this.bank_accounts.find(_ => _.account_type === bankDetails.account_type) || {}).id;
        if(disbursalAccountId && disbursalAccountId !== -1) {
            return this.setIn(["bank_accounts", disbursalAccountId], new BankAccountRecord({...bankDetails, id: disbursalAccountId}));
        }
        if(this.bank_accounts && (this.bank_accounts.size || this.bank_accounts.length)) {
            return this.set("bank_accounts", new List([...this.bank_accounts.toList().toArray(), new BankAccountRecord(bankDetails)]).toMap());
        } else {
            return this.set("bank_accounts", new List([new BankAccountRecord(bankDetails)]).toMap());
        }
    }

    getJSONvalues() {
        return {
            id: this.id,
            gstin: this.gstin,
            pan: this.pan,
            name: this.name,
            short_name: this.short_name,
            type: this.type,
            channel_products: this.channel_products.map(_ => _.id),
            mis_email_recipients: this.mis_email_recipients,
            primary_relationship_manager: this.primary_relationship_manager && this.primary_relationship_manager.id,
            vp_sales:
                this.vp_sales == null
                    ? null
                    : typeof this.vp_sales === "number"
                        ? this.vp_sales
                        : this.vp_sales.id,
            zonal_head:
                this.zonal_head == null
                    ? null
                    : typeof this.zonal_head === "number"
                        ? this.zonal_head
                        : this.zonal_head.id,
            parent_industry:
                this.parent_industry == null
                    ? null
                    : typeof this.parent_industry === "number"
                        ? this.parent_industry
                        : this.parent_industry.id,
            industry_head: (() => {
                const pi = this.parent_industry;
                if (pi == null || typeof pi === "number") {
                    return null;
                }
                const ih = pi.industry_head;
                if (ih == null) {
                    return null;
                }
                return typeof ih === "number" ? ih : ih.id;
            })(),
            secondary_relationship_manager: this.secondary_relationship_manager && this.secondary_relationship_manager.map(_ => +_),
            program_anchor: this.program_anchor && this.program_anchor.id
        };
    }

}



export class BankAccountRecord extends Record({
    id: null,
    account_holder_name: null,
    bank_branch: null,
    company: null,
    associate: null,
    account_type: null,
    account_number: null,
    account_limit: null,
    status: null,
    bank_cheques: new List([]),
    bank_nach: new List([]),
    is_active: null,
    is_key_account: false,
    created_by: null,
    modified_by: null,
    create_date: null,
    modified_date: null
}) {
    get description_list() {
        const list = new OrderedMap({});

        return list.withMutations(_ =>
            _.set(
                "A/C Type",
                ACCOUNT_TYPES.findKey(value => value === this.account_type)
            )
                .set("A/C Holder Name", this.account_holder_name)
                .set("A/C Number", this.account_number)
                .set("A/C Limit", this.account_limit)
                .set("IFSC", this.ifsc_code)
                .set("Bank", this.bank_name)
                .set("Branch", this.branch_name)
        );
    }

    get name() {
        return `${this.bank_branch.bank_name}-${this.bank_branch.name} (${this.bank_branch.ifsc_code})`;
    }
    get ifsc_code() {
        return this.bank_branch && this.bank_branch.ifsc_code;
    }

    get bank_name() {
        return this.bank_branch && this.bank_branch.bank_name;
    }

    get branch_name() {
        return this.bank_branch && this.bank_branch.name;
    }

    get bank_branch_id() {
        return this.bank_branch && this.bank_branch.id ? this.bank_branch.id : this.bank_branch;
    }

    get form() {
        return this.toSeq()
            .filter((_, key) =>
                [
                    "company",
                    "account_holder_name",
                    "account_type",
                    "account_number",
                    "associate",
                    "bank_branch"
                ].includes(key)
            )
            .set("ifsc_code", this.bank_branch)
            .toMap();
    }

    get formValues() {
        return this.toSeq()
            .filter((_, key) =>
                [
                    "id",
                    "company",
                    "associate",
                    "account_type",
                    "account_number",
                    "account_holder_name",
                    "account_limit",
                    "status",
                    "is_key_account"
                ].includes(key)
            )
            .toMap()
            .set("bank_branch", this.bank_branch_id)
            .toJS();
    }

    get JSONValues() {
        return {
            company: this.company,
            id: this.id,
            account_holder_name: this.account_holder_name,
            account_number: this.account_number,
            bank_branch: this.bank_branch,
            account_type: this.account_type
        };
    }
}

export class AddressRecord extends Record({
    id: null,
    company: null,
    associate: null,
    address_type: null,
    ownership_type: null,
    residing_since: null,
    address_line_1: null,
    address_line_2: null,
    city: null,
    district: null,
    state: null,
    country: null,
    pincode: null,
    is_pv_done: false,
    is_communication_address: null
}) {
    get formValues() {
        return this.toSeq()
            .toMap()
            .filter((_, key) =>
                [
                    "id",
                    "company",
                    "associate",
                    "address_type",
                    "ownership_type",
                    "address_line_1",
                    "address_line_2",
                    "pincode",
                    "is_pv_done",
                    "is_communication_address"
                ].includes(key)
            )
            .set(
                "residing_since",
                this.residing_since
                    ? this.residing_since.format("YYYY-MM-DD")
                    : null
            )
            .set("city", this.city_id)
            .set("state", this.state_id)
            .set("country", 1)
            .set("district", this.district_id)
            .toJS();
    }

    get formatted_string() {
        return `${this.address_line_1},
        ${this.address_line_2 ? this.address_line_2 + "," : ""}
        ${this.city_name ? this.city_name + "," : ""}${this.district_name ? this.district_name + "," : ""}${this.state_name ? this.state_name + "," : ""}
        ${this.country_name}-${this.pincode} (${this.ownership})`;
    }

    get city_id() {
        if(this.city && this.city.id) {
            return this.city.id;
        } else if(this.city && this.city.name && !this.city.id) {
            let district = this.district && this.district.id;
            if(!district) {
                district = this.district.name;
            }
            return {
                name: this.city.name,
                district: district,
                state: this.state_id
            };
        }
        return this.city ? this.city.id : null;
    }
    get state_id() {
        return this.state ? this.state.id : null;
    }
    get district_id() {
        return this.district ? this.district.id : null;
    }

    get city_name() {
        return this.city && this.city.name;
    }
    get state_name() {
        return this.state && this.state.name;
    }
    get district_name() {
        return this.district && this.district.name;
    }

    get country_name() {
        return this.country && this.country.name;
    }

    get ownership() {
        return (
            this.ownership_type &&
            RESIDENCE_TYPES.flip().find(
                (value, key) => key === this.ownership_type
            )
        );
    }
    get residing_date() {
        return this.residing_since && this.residing_since.format("DD/MM/YYYY");
    }
}

export class AssociateRecord extends Record({
    id: null,
    company: null,
    type: null,
    nature_of_association: null,
    first_name: null,
    middle_name: null,
    last_name: null,
    email: null,
    email_2: null,
    phone: null,
    phone_office: null,
    phone_residence: null,
    pan: null,
    aadhar: null,
    din: null,
    passport: null,
    passport_issue_date: null,
    passport_expiry_date: null,
    father_name: null,
    mother_name: null,
    spouse_name: null,
    dob: null,
    title: null,
    gender: null,
    category: null,
    nationality: null,
    education: null,
    marital_status: null,
    gross_annual_income: null,
    vintage_current_business_months: null,
    total_experience_months: null,
    profession: null,
    no_of_dependent_adult: null,
    no_of_dependent_children: null,
    is_key_contact: false,
    create_date: null,
    modified_date: null,
    created_by: null,
    modified_by: null,
    cibil_remarks: null,
    cibil_score: null,
    personal_cibil_score: null,
    cibil_report: null,
    cibil_supported_report: new List([]),
    is_main_borrower: false,
    shareholding: null,
    annual_income: null,
    is_active: null,
    is_d2r_guarantor: null,
    is_db_mou_triggered: null,
    key_source : null
}) {
    get key() {
        return this.id;
    }
    toDisplayForm(residence_address) {
        return new Map({})
            .set("type", APPLICANT_TYPES.findKey(_ => _ === this.type))
            .set("name", this.name)
            .set("aadhar", this.aadhar)
            .set("marital_status", this.marital_status)
            .set("profession", this.profession)
            .set("father_name", this.father_name)
            .set("spouse_name", this.spouse_name)
            .set("gender", GENDER_TYPES.findKey(_ => _ === this.gender) || "--")
            .set(
                "dob",
                this.dob && this.dob.format("DD-MM-YYYY") === "Invalid Date"
                    ? "--"
                    : this.dob && this.dob.format("DD-MM-YYYY")
            )
            .set("phone", this.phone)
            .set("phone_office", this.phone_office)
            .set("phone_residence", this.phone_residence)
            .set("email", this.email)
            .set(
                "nature_of_association",
                NATURE_OF_ASSOCIATION_TYPES.findKey(
                    _ => _ === this.nature_of_association
                )
            )
            .set("pan", this.pan)
            .set("is_main_borrower", this.is_main_borrower ? "Yes" : "No")
            .set("residence_address", residence_address)
            .set("annual_income", this.annual_income)
            .set(
                "shareholding",
                this.shareholding >= 0 ? this.shareholding : "--"
            );
    }

    get formValues() {
        return this.toSeq()
            .toMap()
            .filter((_, key) =>
                [
                    "id",
                    "type",
                    "cibil_score",
                    "aadhar",
                    "cibil_remarks",
                    "cibil_supported_report",
                    "personal_cibil_score",
                    "company",
                    "first_name",
                    "middle_name",
                    "last_name",
                    "father_name",
                    "spouse_name",
                    "pan",
                    "phone",
                    "phone_office",
                    "phone_residence",
                    "email",
                    "dob",
                    "gender",
                    "nature_of_association",
                    "is_main_borrower",
                    "shareholding",
                    "annual_income",
                    "marital_status",
                    "profession"
                ].includes(key)
            )
            .set("dob", this.dob ? this.dob.format("YYYY-MM-DD") : "")
            .set(
                "cibil_report",
                this.cibil_report ? this.cibil_report.id : null
            )
            .set(
                "cibil_supported_report",
                this.cibil_supported_report &&
                    (this.cibil_supported_report.size ||
                        this.cibil_supported_report.length) > 0
                    ? this.cibil_supported_report.map(_ => _.id)
                    : []
            )
            .toJS();
    }

    get name() {
        return this.middle_name
            ? this.last_name
                ? `${this.first_name} ${this.middle_name} ${this.last_name}`
                : `${this.first_name} ${this.middle_name}`
            : this.last_name
                ? `${this.first_name} ${this.last_name} `
                : `${this.first_name}`;
    }

    get nameValue() {
        if (!this.first_name && !this.last_name) {
            return null;
        }
        return this.toSeq()
            .toMap()
            .filter((_, key) =>
                ["first_name", "middle_name", "last_name"].includes(key)
            )
            .toJS();
    }

    get option() {
        return {
            label: this.name,
            value: this.id
        };
    }

    get CIBIL_Details() {
        return {
            id: this.id,
            name: this.name,
            cibil_score: this.cibil_score,
            cibil_remarks: this.cibil_remarks,
            cibile_report: this.cibil_report,
            cibil_supported_report: this.cibil_supported_report
        };
    }

    get dob_and_age() {
        return this.dob ? `${moment().diff(this.dob, "years")} years` : null;
    }

    getName() {
        return this.middle_name
            ? this.last_name
                ? `${this.first_name} ${this.middle_name} ${this.last_name}`
                : `${this.first_name} ${this.middle_name}`
            : this.last_name
                ? `${this.first_name} ${this.last_name} `
                : `${this.first_name}`;
    }
    getProfileItem() {
        return {
            key: this.id,
            id: this.id,
            name: this.getName(),
            type: APPLICANT_TYPES.findKey(_ => _ === this.type),
            primary_email: this.primary_email,
            nature_of_association: NATURE_OF_ASSOCIATION_TYPES.findKey(
                _ => _ === this.nature_of_association
            ),
            phone: this.phone
        };
    }

    get max_cibil_score() {
        if (this.personal_cibil_score && this.personal_cibil_score > this.cibil_score) {
            return this.personal_cibil_score;
        }
        return this.cibil_score;
    }


}

export class SpocUserRecord extends Record({
    id: null,
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    gender: null,
    designation: null,
    nature_of_association: null,
    is_key_contact: true,
    is_key_account: true,
    company: null
}) {
    getJSONvalues() {
        return {
            id: null,
            first_name: this.first_name,
            last_name: this.last_name,
            email: this.email,
            phone: this.phone,
            gender: this.gender,
            designation: this.designation,
            nature_of_association: this.nature_of_association,
            type: 1,
            is_key_account: true,
            is_key_contact: true,
            company: this.company
        };
    }
}

export class CompanyLosProfileRecord extends Record({
    id: null,
    name: null,
    type: null,
    gstin: null,
    is_borrower: false,
    no_of_cases_as_seller: 0,
    no_of_cases_as_anchor: 0,
    gst_state: null,
    registered_state: null,
    approved_limit: null,
    throughput: null,
    territories_managed: null,
    company_hq: null,
    total_no_of_dealers: null,
    dealers_with_regular_billing: null,
    cin: null,
    tin: null,
    registration_num: null,
    mis_email_recipients: [],
    current_status: null,
    current_stage : null,
    channel_products: null,
    ledger_name: null,
    primary_relationship_manager: null,
    secondary_relationship_manager: null,
    vp_sales: null,
    primary_rm: null,
    primary_brands: null,
    anchor: null,
    erp: null,
    is_synced: null,
    aggrement_attached: null,
    recommendation_letter_attached: null,
    credit_note_attached: null,
    disbursal_account_detail_updated: null,
    company_turnover: null,
    total_retailers: null,
    retailers_qualified: null,
    sales_coverage: null,
    registered_state_name: null,
    brands: null,
    disbursal_details_updated: null,
    is_anchor_aligned: null,
}) {
    get JSONvalues() {
        return {
            gstin: this.gstin,
            territories_managed: this.territories_managed,
            company_hq: this.company_hq,
            total_no_of_dealers: this.total_no_of_dealers,
            dealers_with_regular_billing: this.dealers_with_regular_billing,
            type: this.type,
            cin: this.cin,
            tin: this.tin,
            registration_num: this.registration_num,
            mis_email_recipients: this.mis_email_recipients && this.mis_email_recipients.length ? this.mis_email_recipients.split(",") : [],
            current_status: this.current_status,
            ledger_name: this.ledger_name,
            primary_relationship_manager: this.primary_relationship_manager && this.primary_relationship_manager.id,
            vp_sales: this.vp_sales && this.vp_sales.id,
            channel_products: this.channel_products && this.channel_products.map(_ => _.id),
            secondary_relationship_manager: this.secondary_relationship_manager && this.secondary_relationship_manager.map(_ => _.id)
        };
    }
}


export class MarginSubventionRecord extends Record({
    id: null,
    withhold_perc: null,
    upfront_interest_days: null,
    subvention_days: null,
    exigency_days: null,
    due_date_from_type: null,
    upfront_interest_from_type: null
}) {
    get marginDetail() {
        return [
            ["Subvention Days", this.upfront_interest_days],
            ["Cashback Days", this.subvention_days],
            ["Exigency Days", this.exigency_days],
            ["Due Date From", this.due_date_from_type + " Date"],
            ["Upfront Interest From", this.upfront_interest_from_type]
        ];
    }
}

export class CashbackSplitRecord extends Record({
    id: null,
    seller_share: null,
    buyer_share: null,
    exigency_share: null
}){
    get cashbackDetail() {
        return [
            ["Buyer Share", this.buyer_share],
            ["Seller Share", this.seller_share],
            ["Exigency Share", this.exigency_share]
        ];
    }
}

export class InterestDetailRecord extends Record({
    id: null,
    description: null,
    normal_roi: null,
    overdue_roi: null,
    roi_excess_principal: null,
    roi_incentive: null,
    interest_configuration: new List([])
}){
    get interestDetail() {
        return [
            ["Normal ROI", this.normal_roi],
            ["Overdue ROI", this.overdue_roi],
            ["Excess ROI", this.roi_excess_principal],
            ["Incentive ROI", this.roi_incentive]
        ];
    }
}


export class TenorDetailRecord extends Record({
    id: null,
    tenor_days: null,
    soft_tenor_days: null,
    cooling_period_days: null,
    light_overdue_days: null,
    interest_tenor_days: null,
    fee_tenor_days: null
}){
    get rffDetail() {
        return [
            ["Total Tenor", this.tenor_days],
            ["Soft Tenor", this.soft_tenor_days],
            ["Cooling Period", this.cooling_period_days],
            ["Light Overdue Period", this.light_overdue_days]
        ];
    }
}

export class WithholdPercMasterRecord extends Record({
    id: null,
    withhold_perc: null,
    is_seller_master: null
}) {}

export class CompanySellerSchemeRecord extends Record({
    id: null,
    name: null,
    description: null,
    date_of_agreement: null,
    product: null,
    disbursal_wait_days: null,
    withhold_perc_master: null,
    tenor_master: new TenorDetailRecord(),
    rff_disbursal_configuration: new MarginSubventionRecord(),
    rff_cash_back_split_master: new CashbackSplitRecord(),
    interest_master: new InterestDetailRecord(),
    account_block_checks: null,
    rff_flow_configuration: null
}) {
    get basicDetails() {
        return [
            ["Date of Agreement", this.date_of_agreement && moment(this.date_of_agreement).format("DD/MM/YYYY") ],
            ["Product", this.product && this.product.name],
            ["Disbursal Wait Days", this.disbursal_wait_days],
            ["Withhold Percentage", this.withhold_perc_master && this.withhold_perc_master.withhold_perc]
        ];
    }
}

export class SchemeDetailsRecord extends Record({
    id: null,
    name: null,
    roi: null,
    margin: null,
    overdue_roi: null,
    overdue_tenor: null,
    tenor: null,
    subvention_perc: null,
    subvention_days: null,
    disbursal_wait_days: null,
    cooling_period: null,
    date_of_agreement: null,
    product: null,
    lending_partner: null,
    channel_product: null,
    region: null,
    is_active: null,
    cashback_sharing_percentage_for_dealer: null,
    cashback_sharing_percentage_for_seller: null,
    cashback_sharing_percentage_for_progcap: null,
    deviation_allowed: null,
    limit_days: null,
    cashback_days: null,
    exigency_days: null,
    subvention_from: null,
    roi_per_day: null,
    buyer_max_limit: null,
    stop_billing_days: null,
    roiStructure: null
}) {

    get channel_product_names() {
        return (this.channel_product && this.channel_product.map(_ => _.name).join(", ")) || "---";
    }
    
    get _region() {
        return (this.region && this.region.map(_ => _.name).join(", ")) || "---";
    }

    get agreementDate() {
        if(this.date_of_agreement) {
            return moment(this.date_of_agreement).format("MMM DD, YYYY");
        }
        return "--"; 
    }

    get productName() {
        if(this.product) {
            return this.product.name;
        }
        return "---";
    }

    get lendingPartnerName() {
        if(this.lending_partner) {
            return this.lending_partner.name;
        }
        return "--";
    }

    get subventionFrom() {
        return SUBVENTION_FROM[this.subvention_from] || "--";
    }

    get leftColumns() {
        return [
            ["Product", this.productName],
            ["Lending Partner", this.lendingPartnerName],
            ["Region", this._region],
            ["Channel Product", this.channel_product_names || "----"]
        ];
    }

    get rightColumns() {
        return [
            ["Tenor in days", (this.tenor && this.tenor+" days") || "--"],
            ["Overdue Interest Tenor", (+this.overdue_tenor && this.overdue_tenor + " days") || "--"],
            ["ROI", (+this.roi && this.roi+"%") || "--"],
            ["Overdue ROI", (+this.overdue_roi && this.overdue_roi+"%") || "--"],
            ["Margin", (+this.margin && this.margin+"%") || "--"],
            ["Disbursal Wait days", (this.disbursal_wait_days && this.disbursal_wait_days+" days") || "--"],
            ["Cooling Period", (this.cooling_period && this.cooling_period+" days") || "--"],
            ["Subvention Days", (this.subvention_days && this.subvention_days+" days") || "--"],
            ["Cashback Days", (this.cashback_days && this.cashback_days + " days") || "--"],
            ["Exigency Days", (this.exigency_days && this.exigency_days + " days") || "--"],
            ["Subvention Percentage", (+this.subvention_perc && this.subvention_perc + "%") || "--"],
            ["Subvention From", this.subventionFrom],
            ["Cashback Sharing % Dealer", (+this.cashback_sharing_percentage_for_dealer && this.cashback_sharing_percentage_for_dealer+ "%") || "--"],
            ["Cashback Sharing % Seller", (+this.cashback_sharing_percentage_for_seller && this.cashback_sharing_percentage_for_seller+ "%") || "--"],
            ["Cashback Sharing % Progcap", (+this.cashback_sharing_percentage_for_progcap && this.cashback_sharing_percentage_for_progcap+ "%") || "--"],
            ["Stop Billing Days", (this.stop_billing_days && this.stop_billing_days + " days") || "--"]
        ];
    }
    
    get newSchemeFirstSection() {
        return [
            ["Tenor in days", (this.tenor && this.tenor+" days") || "--"],
            ["Margin", (+this.margin && this.margin+"%") || "--"],
            ["Overdue Tenor", (+this.overdue_tenor && this.overdue_tenor + " days") || "--"],
            ["ROI Structure", (+this.roiStructure && this.roiStructure+"%") || "Flat"],
            ["Normal ROI", (+this.roi && this.roi+"%") || "--"],
            ["Overdue ROI", (+this.overdue_roi && this.overdue_roi+"%") || "--"],
        ];
    }

    get newSchemeRoiSchedule() {
        return [
            ["From Day", "--"],
            ["To Day", "--"],
            ["ROI", "--"]
        ];
    }

    get newSchemeSecThree(){
        return [
            ["Stop Billing Days", (this.stop_billing_days && this.stop_billing_days + " days") || "--"],
            ["Cooling Period", (this.cooling_period && this.cooling_period+" days") || "--"],
            ["Cashback Sharing % Dealer", (+this.cashback_sharing_percentage_for_dealer && this.cashback_sharing_percentage_for_dealer+ "%") || "--"],
            ["Cashback Sharing % Seller", (+this.cashback_sharing_percentage_for_seller && this.cashback_sharing_percentage_for_seller+ "%") || "--"],
            ["Cashback Sharing % Progcap", (+this.cashback_sharing_percentage_for_progcap && this.cashback_sharing_percentage_for_progcap+ "%") || "--"],
        ];
    }

    get newSchemeEditFirstSec(){
        return [
            ["Disbursal Wait days", (this.disbursal_wait_days && this.disbursal_wait_days+" days") || "--"],
            ["Subvention Percentage", (+this.subvention_perc && this.subvention_perc + "%") || "--"],
            ["Subvention Days", (this.subvention_days && this.subvention_days+" days") || "--"]
        ];
    }

    get newSchemeFlexibleParam() {
        return [
            ["Disbursal Wait days", (this.disbursal_wait_days && this.disbursal_wait_days+" days") || "--"],
            ["Subvention Percentage", (+this.subvention_perc && this.subvention_perc + "%") || "--"],
            ["Subvention Days", (this.subvention_days && this.subvention_days+" days") || "--"]
        ];
    }

    get schemeDetails() {
        return [
            ["Cashback Days", this.cashback_days],
            ["Exigency Days", this.exigency_days],
            ["Cashback Sharing % Dealer", this.cashback_sharing_percentage_for_dealer],
            ["Cashback Sharing % Seller", this.cashback_sharing_percentage_for_seller],
            ["Cashback Sharing % Progcap", this.cashback_sharing_percentage_for_progcap],
            ["Name", this.name],
            ["Region", this._region],
            ["ROI", this.roi],
            ["Lending Partner", this.lendingPartnerName],
            ["Overdue ROI", this.overdue_roi],
            ["Overdue Tenor", this.overdue_tenor],
            ["Margin", this.margin],
            ["Tenor", this.tenor],
            ["Subvention Percentage", this.subvention_perc],
            ["Subvention Days", this.subvention_days],
            ["Subvention From", this.subventionFrom],
            ["Disbursal Wait days", this.disbursal_wait_days],
            ["Cooling Period", this.cooling_period],
            ["Date of Agreement", this.agreementDate],
            ["Product", this.productName],
            ["Channel Product", this.channel_product_names],
            ["Buyer Max Limit", this.buyer_max_limit],
            ["ROI Per Day", this.roi_per_day],
            ["Stop Billing Days", this.stop_billing_days]

        ];
    }
}

export class NewSchemeDetailsRecord extends Record({
    id: null,
    name: null,
    min_normal_roi: null,
    margin: null,
    max_overdue_roi: null,
    min_normal_roi_buyer: null,
    min_min_normal_roi: null,
    min_normal_roi_seller: null,
    overdue_tenor: null,
    tenor: null,
    subvention_perc: null,
    subvention_days: null,
    subvention_per_unit: null,
    unit: null,
    disbursal_wait_days: null,
    cooling_period: null,
    date_of_agreement: null,
    product: null,
    lending_partner: null,
    channel_product: null,
    is_active: null,
    cashback_sharing_percentage_for_dealer: null,
    cashback_sharing_percentage_for_seller: null,
    cashback_sharing_percentage_for_progcap: null,
    cashback_days: null,
    exigency_days: null,
    subvention_from: null,
    roi_per_day: null,
    buyer_max_limit: null,
    stop_billing_days: null,
    roiStructure: null,
    roi_schedule: null,
    dwd_sp_all: null,
    is_default_scheme: null,
    dwd_sp_default: null,
    is_roi_structure_flat: null,
    cash_cards_schedule: null,
    scheme_type: null,
    term_loan_parameters: null,
    progcap_subvention_days: null,
    subvention_by: null,
    anchor: null,
    deviation_allowed: null,
    limit_days: null,
    invoice_level_tenure: null,
    invoice_level_roi: null,
    interest_repayment_from: null,
    interest_posting_event: null,
    proportion_of_payment_by_vendor: null,
    normal_roi_schedule: null,
    is_adhoc_applicable_on_overdue: null,
    additional_roi: null,
    security_deposit: null,
    is_interest_posting_before_moratorium: null,
    start_date: null,
    end_date: null,
    closed_by: null,
    scheme_closed_by: null,
    scheme_created_by: null,
}) {

    get _dwd_sp_default() {
        if(this.dwd_sp_default){
            return this.dwd_sp_default;
        }else{
            return {};
        }
    }

    get lendingPartnerName() {
        if(this.lending_partner) {
            return this.lending_partner.short_name;
        }
        return "--";
    }

    get _is_default_scheme() {
        return (this.is_default_scheme);
    }

    get _dw_sp_all () {
        return (this.dwd_sp_all || []);
    }

    get new_dw_sp_all () {
        return (this._dw_sp_all);
    }

    get _roi_schedule() {
        return (this.roi_schedule || []); 
    }

    get _roi_schedule_arr() {
        return(
            this._roi_schedule
        );
    }

    get_interest_repayment_from(){
        if(this.interest_repayment_from == 1){
            return "Anchor"
        }else if(this.interest_repayment_from == 2){
            return "Vendor"
        }else if(this.interest_repayment_from == 3){
            return "Both"
        }
    }

    get_interest_posting_event(){
        if(this.interest_posting_event == 1 ){
            return "Interest Deduction At Disbursal"
        }else if(this.interest_posting_event == 2){
            return "Interest Posting At Month End"
        }else if(this.interest_posting_event == 3 ){
            return "Interest Posting At Due Date"
        }
    }    

    get newSchemeFirstSection() {
        if(this.scheme_type == "8"){
            return [
                ["Margin", (+this.margin && this.margin+"%") || "--", "infotext"],
                ["Tenor (days)", (this.tenor && this.tenor+" days") || "--", "infotext lorem ipsum"],
                ["Overdue Tenor (days)", (+this.overdue_tenor && this.overdue_tenor + " days") || "--"],
                ["ROI Structure", this.is_roi_structure_flat && this.is_roi_structure_flat ? "Flat" : "Tiered"],
                ["Normal ROI", (+this.min_normal_roi && this.min_normal_roi+"%") || "--"],
                ["Overdue ROI", (+this.max_overdue_roi && this.max_overdue_roi+"%") || "--"],
                ["Invoice Level Tenure", this.invoice_level_tenure ? "Yes" : "No" || "--"],
                ["Invoice Level ROI", this.invoice_level_roi ? "Yes" : "No" || "--"],
                ["Interest Repayment From", this.get_interest_repayment_from() || "--"],
                ["Interest Posting Event", this.get_interest_posting_event() || "--"],
                ["Proportion of Payment By Vendor", this.proportion_of_payment_by_vendor || "--"],
                ["Actual Overdue ROI", this.additional_roi || "--"],
                ["Is Adhoc Applicable On Overdue" ,this.is_adhoc_applicable_on_overdue ? "Yes" : "No"]
            ];
        }
        return [
            ["Margin", (+this.margin && this.margin+"%") || "--", "infotext"],
            ["Tenor (days)", (this.tenor && this.tenor+" days") || "--", "infotext lorem ipsum"],
            ["Overdue Tenor (days)", (+this.overdue_tenor && this.overdue_tenor + " days") || "--"],
            ["ROI Structure", this.is_roi_structure_flat && this.is_roi_structure_flat ? "Flat" : "Tiered"],
            ["Normal ROI", (+this.min_normal_roi && this.min_normal_roi+"%") || "--"],
            ["Overdue ROI", (+this.max_overdue_roi && this.max_overdue_roi+"%") || "--"],
            ["Actual Overdue ROI", this.additional_roi || "--"],
            ["Is Adhoc Applicable On Overdue" ,this.is_adhoc_applicable_on_overdue ? "Yes" : "No"]
        ];
    }

    get newTermLoanSchemeFirstSection() {
        return [
            ["Tenor (days)", (this.tenor && this.tenor+" days") || "--", "infotext lorem ipsum"],
            ["Overdue Tenor (days)", (+this.overdue_tenor && this.overdue_tenor + " days") || "--"],
            ["ROI Structure", this.is_roi_structure_flat && this.is_roi_structure_flat ? "Flat" : "Tiered"],
        ];
    }

    get newSchemeThreeFirstSection() {
        return [
            ["Margin", (+this.margin && this.margin+"%") || "--", "infotext"],
            ["Tenor (days)", (this.tenor && this.tenor+" days") || "--", "infotext lorem ipsum"],
            ["Overdue Tenor (days)", (+this.overdue_tenor && this.overdue_tenor + " days") || "--"],
            ["ROI Structure", this.is_roi_structure_flat && this.is_roi_structure_flat ? "Flat" : "Tiered"],
            ["Normal ROI", (+this.min_normal_roi && this.min_normal_roi+"%") || "--"],
            ["Overdue ROI", (+this.max_overdue_roi && this.max_overdue_roi+"%") || "--"],
            ["Buyer ROI", (+this.min_normal_roi_buyer && this.min_normal_roi_buyer+"%") || "--"],
            ["Seller ROI", (+this.min_normal_roi_seller && this.min_normal_roi_seller+"%") || "--"],
            ["Actual Overdue ROI", this.additional_roi || "--"],
            ["Is Adhoc Applicable On Overdue" ,this.is_adhoc_applicable_on_overdue ? "Yes" : "No"]
        ];
    }

    get newSchemeRoiSchedule() {
        return [
            ["From Day", "--"],
            ["To Day", "--"],
            ["ROI", "--"]
        ];
    }

    get newSchemeSecThree(){
        return [
            ["Stop Billing Days", (this.stop_billing_days && this.stop_billing_days + " days") || "--"],
            ["Cooling Period", (this.cooling_period && this.cooling_period+" days") || "--"],
            ["Subvention By", (this.subvention_by && this.subvention_by) || "--"],
            ["Progcap Subvention Days", (this.progcap_subvention_days && this.progcap_subvention_days+" days") || "--"],
            ["Cashback to Dealer", (+this.cashback_sharing_percentage_for_dealer && this.cashback_sharing_percentage_for_dealer+ "%") || "--"],
            ["Cashback to Seller", (+this.cashback_sharing_percentage_for_seller && this.cashback_sharing_percentage_for_seller+ "%") || "--"],
            ["Cashback to Progcap", (+this.cashback_sharing_percentage_for_progcap && this.cashback_sharing_percentage_for_progcap+ "%") || "--"],
        ];
    }

    get newSchemeEditFirstSec(){
        return [
            ["Disbursal Wait days", (this.disbursal_wait_days && this.disbursal_wait_days+" days") || "--"],
            ["Subvention %", (+this.subvention_perc && this.subvention_perc + "%") || "--"],
            ["Subvention Days", (this.subvention_days && this.subvention_days+" days") || "--"]
        ];
    }

    get newSchemeFlexibleParam() {
        return [
            ["Disbursal Wait days", (this.disbursal_wait_days && this.disbursal_wait_days+" days") || "--"],
            ["Subvention %", (+this.subvention_perc && this.subvention_perc + "%") || "--"],
            ["Subvention Days", (this.subvention_days && this.subvention_days+" days") || "--"]
        ];
    }

}

export class FeeRecord extends Record({
    id: null,
    lending_partner: null,
    fee_master: null,
    charge_at: null,
    from_date: null,
    to_date: null,
    business_product_type: null,
    is_default_configuration: false
}) {

    get feeMasterId() {
        if(this.fee_master) {
            return this.fee_master.id;
        }
        return "";
    }

    get chargeAt() {
        const data = new OrderedMap({
            1:{id: 1, name: "At Account Creation"},
            2:{id: 2, name: "At First Disbursal"},
            3:{id: 3, name: "Manually"}
        });
        return data.find((_, k) => k == this.charge_at );
    }

    get feeFormData() {
        return {
            id: this.id,
            lending_partner: this.lending_partner,
            charge_at: this.chargeAt,
            business_product_type: this.business_product_type
        };
    }
}